import { Formik } from 'formik';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Drawer, Field, Form } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck } from '~/hooks';
import { dateFormats, emptyStringToNull, mergeValues } from '~/utils';
import { revenueTypes } from './revenueTypes';

export default function RevenueAdjustmentForm({ revenueAdjustmentId, projectId, currency, onSaved, onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();

  const [revenueAdjustment, setRevenueAdjustment] = useState(null);
  useEffect(() => {
    (async () => {
      if (!revenueAdjustmentId) {
        setRevenueAdjustment({});
        return;
      }

      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .revenueAdjustments(revenueAdjustmentId)
          .get();
        setRevenueAdjustment(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId, revenueAdjustmentId]);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  if (!revenueAdjustment) return null;

  const initialValues = mergeValues(
    {
      date: moment().format(dateFormats.isoDate),
      revenueType: '',
      amount: '',
      notes: '',
    },
    revenueAdjustment,
  );

  const handleSubmit = async (values, formik) => {
    formik.setSubmitting(true);
    values = emptyStringToNull(values);

    try {
      if (revenueAdjustmentId) {
        await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .revenueAdjustments(revenueAdjustmentId)
          .patch(values);
      } else {
        await api.www.workspaces(workspace.id).projectSettings(projectId).revenueAdjustments().post(values);
      }

      toast.success('Earned Revenue Adjustment saved successfully.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message);
    }

    formik.setSubmitting(false);
  };

  return (
    <Drawer
      title={revenueAdjustmentId ? 'Edit Earned Revenue Adjustment' : 'Add Earned Revenue Adjustment'}
      isOpen
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              date: Yup.date().label('Date').nullable().required(),
              revenueType: Yup.string().label('Revenue Type').oneOf(Object.keys(revenueTypes)).required(),
              amount: Yup.number().label('Amount').min(-99999999999).max(99999999999).nullable().required(),
              notes: Yup.string().label('Notes').max(255),
            })}>
            {(formik) => (
              <Form>
                <Form.Section title="Details">
                  <Form.Control>
                    <Field.DayPicker name="date" placeholder="Date" clearable={false} />
                  </Form.Control>

                  <Form.Control>
                    <Field.SingleSelect name="revenueType" placeholder="Revenue Type">
                      {Object.entries(revenueTypes).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </Field.SingleSelect>
                  </Form.Control>

                  <Form.Control>
                    <Field.Currency name="amount" placeholder="Amount" currency={currency} />
                  </Form.Control>

                  <Form.Control>
                    <Field.Text name="notes" placeholder="Notes" maxLength={255} />
                  </Form.Control>
                </Form.Section>

                <Drawer.Actions>
                  <Buttons align="right">
                    <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                    <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                      Save & Close
                    </Button>
                  </Buttons>
                </Drawer.Actions>
              </Form>
            )}
          </Formik>
        );
      }}
    </Drawer>
  );
}
